import React from "react";
import PropTypes from "prop-types";
import { MessageIcon } from "../../../design-system";
import { borders, surfaces, textColors, typography } from "../../../design-system/tokens";
import { formatEmailVariant } from "../automations/automationUtils";
import { getChannelMeta } from "./messagesUtils";

const chipClass = `inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${borders.glass} ${surfaces.glassCard} ${typography.caption} ${textColors.secondary} whitespace-nowrap`;

/** Inline envelope (no mail icon in the design-system icon set). */
const EnvelopeIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

EnvelopeIcon.propTypes = { className: PropTypes.string };

/** Inbox / Email chip with its icon. */
const ChannelChip = ({ channel, className = "" }) => {
  const meta = getChannelMeta(channel);
  return (
    <span className={`${chipClass} ${className}`} title={meta.description || undefined}>
      {channel === "email" ? (
        <EnvelopeIcon className="w-3.5 h-3.5 shrink-0" />
      ) : (
        <MessageIcon size="xs" className="shrink-0" aria-hidden="true" />
      )}
      {meta.label}
    </span>
  );
};

ChannelChip.propTypes = {
  channel: PropTypes.string,
  className: PropTypes.string,
};

/**
 * Email template variant chip — "Default" / "Marketing" / "Product update" /
 * "System" for email rows; inbox rows have no template and show "—".
 */
export const TemplateChip = ({ channel, variant, className = "" }) => {
  if (channel !== "email") {
    return (
      <span className={`${typography.caption} ${textColors.muted} ${className}`}>
        —<span className="sr-only">no template (inbox message)</span>
      </span>
    );
  }
  const value = variant || "default";
  return (
    <span className={`${chipClass} ${className}`} title={`Email template: ${formatEmailVariant(value)} (${value})`}>
      {formatEmailVariant(value)}
    </span>
  );
};

TemplateChip.propTypes = {
  channel: PropTypes.string,
  variant: PropTypes.string,
  className: PropTypes.string,
};

export default ChannelChip;
